import { useCallback } from 'react';

import {
  useAttachCareerExerciseTemplateMutation,
  useDetachCareerExerciseTemplateMutation,
  useDropCareerExerciseSubmissionsMutation,
  useErpCareerExerciseTemplatesQuery,
  useSaveCareerExerciseTemplateMutation,
  useToggleCareerExerciseTemplateMutation,
} from 'core/graphql';

import {
  ICareerExerciseTemplate,
  IExerciseFieldDraft,
  ISkillMutationResult,
} from './careerModel';

const EMPTY: ICareerExerciseTemplate[] = [];

const result = (payload: any): ISkillMutationResult => ({
  success: Boolean(payload?.success),
  errors: (payload?.customErrors ?? []).map((item: any) => ({
    field: item?.field ?? '',
    message: item?.message ?? '',
  })),
});

/**
 * ТЗ 0040 фаза B — справочник анкет.
 *
 * `includeHidden` отдельным флагом, а не «показывать всё всегда»: скрытая
 * анкета не предлагается для прикрепления, и в списке по умолчанию ей делать
 * нечего — иначе оператор выберет её и упрётся в отказ сервера.
 */
export const useExerciseTemplates = (
  search?: string,
  includeHidden = false,
) => {
  const { data, loading, refetch } = useErpCareerExerciseTemplatesQuery({
    variables: { search: search || null, includeHidden },
    fetchPolicy: 'cache-and-network',
  });

  return {
    templates: data?.careerExerciseTemplates ?? EMPTY,
    loading,
    refetch,
  };
};

export const useExerciseTemplateMutations = (onDone?: () => void) => {
  const [save, saveState] = useSaveCareerExerciseTemplateMutation();
  const [toggle, toggleState] = useToggleCareerExerciseTemplateMutation();
  const [drop, dropState] = useDropCareerExerciseSubmissionsMutation();

  const done = useCallback(
    (payload: any) => {
      const out = result(payload);
      if (out.success && onDone) onDone();
      return out;
    },
    [onDone],
  );

  return {
    pending: saveState.loading || toggleState.loading || dropState.loading,

    saveTemplate: useCallback(
      async (
        templateId: string | null,
        title: string,
        intro: string,
        outro: string,
        fields: IExerciseFieldDraft[],
      ) => {
        const { data } = await save({
          variables: {
            templateId,
            title,
            intro,
            outro,
            fields: fields.map((field) => ({
              id: field.id,
              label: field.label,
              helpText: field.helpText,
              placeholder: field.placeholder,
              fieldType: field.fieldType,
              required: field.required,
              options: field.options,
              binding: field.binding || undefined,
              bindingLanguage: field.bindingLanguage || undefined,
            })),
          },
        });
        return done(data?.saveCareerExerciseTemplate);
      },
      [save, done],
    ),

    setActive: useCallback(
      async (templateId: string, isActive: boolean) => {
        const { data } = await toggle({ variables: { templateId, isActive } });
        return done(data?.toggleCareerExerciseTemplate);
      },
      [toggle, done],
    ),

    resetSubmissions: useCallback(
      async (templateId: string) => {
        const { data } = await drop({
          variables: { templateId, confirm: true },
        });
        return done(data?.resetCareerExerciseTemplate);
      },
      [drop, done],
    ),
  };
};

/** Прикрепление анкеты к уроку — отдельно от её содержания (ТЗ 0040 фаза B). */
export const useLessonExerciseLink = (onDone?: () => void) => {
  const [attach, attachState] = useAttachCareerExerciseTemplateMutation();
  const [detach, detachState] = useDetachCareerExerciseTemplateMutation();

  const done = useCallback(
    (payload: any) => {
      const out = result(payload);
      if (out.success && onDone) onDone();
      return out;
    },
    [onDone],
  );

  return {
    pending: attachState.loading || detachState.loading,
    attach: useCallback(
      async (lessonId: string, templateId: string) => {
        const { data } = await attach({ variables: { lessonId, templateId } });
        return done(data?.attachCareerExerciseTemplate);
      },
      [attach, done],
    ),
    detach: useCallback(
      async (lessonId: string) => {
        const { data } = await detach({ variables: { lessonId } });
        return done(data?.detachCareerExerciseTemplate);
      },
      [detach, done],
    ),
  };
};
